import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { formatOrdinalDate } from '@/utils/formatOrdinalDate';
import { AdSlot } from '@/components/ads/AdSlot';
import { usePickemContestCardAd } from '@/hooks/usePickemContestCardAd';
import { Button } from '../ui/button';
import { LeaderBoardIcon } from '../icons/LeaderBoardIcon';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface GameCardFullWidthProps {
  id: string;
  title: string;
  prize: string;
  state: GameState;
  gameDate: Date;
  competitionData: any;
  showLeaderBoard: boolean;
  showResults: boolean;
  onViewLeaderboard?: () => void;
  onViewResults?: () => void;
  leagueLogoUrl?: string;
  totalEntries?: number;
  className?: string;
  bgGradientStart?: string;
  bgGradientEnd?: string;
  borderColor?: string;
  showInfoButton?: boolean;
  onInfoClick?: () => void;
  /**
   * Optional element rendered in the top-right of the card in place of the info
   * button — used by the three-dot overflow menu (DKPLAT-664). When provided it
   * takes precedence over `showInfoButton`, so the two never render together.
   */
  headerAction?: React.ReactNode;
  /** Whether the user has reserved a seat (DKPLAT-528) without submitting picks yet. */
  hasReservation?: boolean;
  /**
   * Whether this pickem has any playable questions posted yet. Derived from
   * the real questions array (see type-transformers.ts) — NOT from
   * questionsPostedAt, which is display-only for the date label.
   */
  hasQuestions?: boolean;
  /** ISO timestamp for when questions will be posted, or null. Display-only. */
  questionsPostedAt?: string | null;
  /**
   * Whether the user has submitted real picks (DKPLAT-533). Without this the card
   * could not tell a converted reservation from an unconverted one, so the reserved
   * row vanished the moment questions posted — state 2 of the matrix was unreachable.
   */
  hasSelections?: boolean;
  /** Whether the contest is still open for entry (DKPLAT-533) — mirrors GameCard. */
  isOpen?: boolean;
  /**
   * Picks flow for the reserved row's Edit button (DKPLAT-533). Optional: callers
   * that don't pass it keep the previous always-disabled Edit.
   */
  onEdit?: () => void;
}

const GameCardFullWidth: React.FC<GameCardFullWidthProps> = ({
  id,
  title,
  prize,
  state,
  gameDate,
  competitionData,
  showLeaderBoard,
  showResults,
  onViewLeaderboard,
  onViewResults,
  leagueLogoUrl,
  totalEntries,
  className = '',
  bgGradientStart,
  bgGradientEnd,
  borderColor,
  showInfoButton,
  onInfoClick,
  headerAction,
  hasReservation = false,
  // DKPLAT-533: defaults to FALSE — only read on the reserved paths, where an absent
  // signal must fail closed ("Entry Reserved"), never open onto an enabled Edit.
  hasQuestions = false,
  questionsPostedAt = null,
  hasSelections = false,
  isOpen = true,
  onEdit,
}) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { adPlacement, shouldShowAd } = usePickemContestCardAd(id);

  // DKPLAT-528: hasQuestions is the real "has playable questions" signal
  // (derived upstream from the questions array, mirrors GameCard.tsx).
  // questionsPostedAt is display-only for the date label below.
  const questionsPostedLabel = formatOrdinalDate(questionsPostedAt);
  // DKPLAT-533: the row is keyed on the reservation being UNCONVERTED, not on
  // !hasQuestions. The old condition made the row disappear the instant questions
  // posted, so state 2 ("No Picks Submitted - Make Selections Now!") could never
  // render here. Now matches GameCard.tsx.
  const isReservedUnconverted = hasReservation && !hasSelections;
  const showReservedRow =
    (state === 'countdown' || state === 'upcoming') && isReservedUnconverted && isOpen;
  const showNoPicksLabel = (state === 'live' || state === 'final') && isReservedUnconverted;

  useEffect(() => {
    // Only set up interval for countdown state
    if (state !== 'countdown') {
      return;
    }

    // Initial calculation
    setTimeRemaining(formatTimeRemaining(gameDate));

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;

      // Check if countdown has expired
      if (targetDate.getTime() <= now.getTime()) {
        clearInterval(interval);
        setTimeRemaining(t('game.time.startingSoon'));
      } else {
        setTimeRemaining(formatTimeRemaining(targetDate));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, state, t]);

  const getStartDisplay = () => {
    switch (state) {
      case 'countdown':
        return timeRemaining || formatTimeRemaining(gameDate);
      case 'live':
        return t('game.status.live');
      case 'final':
        return t('game.status.complete');
      default: {
        // Calculate relative time for upcoming
        const now = new Date();
        const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;
        const diff = targetDate.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 0) return `${days} ${days > 1 ? t('game.time.days') : t('game.time.day')}`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} ${hours > 1 ? t('game.time.hours') : t('game.time.hour')}`;
        return t('game.time.soon');
      }
    }
  };

  const isLiveOrFinal = state === 'live' || state === 'final';

  const hasGradient = bgGradientStart || bgGradientEnd;
  const cardStyle: React.CSSProperties = {
    ...(hasGradient && {
      background: `linear-gradient(135deg, ${bgGradientStart ?? bgGradientEnd}, ${bgGradientEnd ?? bgGradientStart})`,
    }),
    ...(borderColor && { borderBottomColor: borderColor }),
  };

  return (
    <div
      data-test-id={`game-card-fullwidth-${id}`}
      data-test-state={state}
      style={cardStyle}
      className={`
        w-full bg-[#242424]
        border-b border-[#373737]
        px-4 py-2.5
        ${className}
      `}
    >
      {/*
        Row 1: League Icon + Title + optional info button.
        The bottom margin only has to separate this from something below it. With the stats
        row gone on an upcoming contest, row 1 is the whole card, and keeping the margin left
        dead space under the title — half of what made the card look tall.
      */}
      <div className={`flex items-start justify-between gap-3 ${isLiveOrFinal ? 'mb-4' : ''}`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <img
            src={leagueLogoUrl || '/league-icon.svg'}
            alt="League icon"
            className="w-12 h-12 object-contain flex-shrink-0"
          />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span
              data-test-id={`game-title-${id}`}
              className="font-gilroy font-bold text-xs text-text-primary tracking-wide uppercase leading-tight"
            >
              {title}
            </span>
            {/*
              The game count used to sit here as "(6 Games)". The prize pool took its place:
              it is the number that decides whether a player enters, and it was previously
              buried in a three-column stats row below. That row is gone for an upcoming
              contest (see below), so the prize is promoted onto this line instead.

              Live and final contests keep their own row — rank, points, current prize — so
              they must not print the prize twice.
            */}
            <span className="text-xs font-gilroy font-semibold">
              <span className="text-[#ABABAB]">Start: </span>
              <span className="text-pickem-primary">{getStartDisplay()}</span>
              {/* Label grey and amount green, matching "Start:" beside it — the label names
                  the figure, the colour is what makes the figure itself stand out. */}
              {!isLiveOrFinal && prize && (
                <>
                  <span className="text-[#ABABAB]"> · Prize Pool: </span>
                  <span data-test-id={`prize-${id}`} className="text-pickem-primary">
                    {prize}
                  </span>
                </>
              )}
            </span>
          </div>
        </div>
        {headerAction}
        {!headerAction && showInfoButton && onInfoClick && (
          <button
            type="button"
            onClick={onInfoClick}
            className="flex-shrink-0 p-1 hover:opacity-80 transition-opacity"
            aria-label="Contest details"
            data-test-id={`contest-info-button-${id}`}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2.3125C14.5678 2.31745 17.029 3.33959 18.8447 5.15527C20.6604 6.97096 21.6825 9.43224 21.6875 12C21.6875 13.916 21.1191 15.7888 20.0547 17.3818C18.9902 18.9749 17.4772 20.217 15.707 20.9502C13.937 21.6833 11.9894 21.8747 10.1104 21.501C8.23116 21.1272 6.50521 20.2044 5.15039 18.8496C3.79557 17.4948 2.87282 15.7688 2.49902 13.8896C2.1253 12.0106 2.31668 10.063 3.0498 8.29297C3.78303 6.52281 5.02506 5.00979 6.61816 3.94531C8.11175 2.94735 9.85119 2.3856 11.6416 2.31934L12 2.3125ZM11.25 10.4375C11.0345 10.4375 10.8282 10.5234 10.6758 10.6758C10.5234 10.8282 10.4375 11.0345 10.4375 11.25C10.4375 11.4655 10.5234 11.6718 10.6758 11.8242C10.8133 11.9618 10.9949 12.0437 11.1875 12.0586V16.5C11.1875 16.7155 11.2734 16.9218 11.4258 17.0742C11.5782 17.2266 11.7845 17.3125 12 17.3125H12.75C12.9655 17.3125 13.1718 17.2266 13.3242 17.0742C13.4766 16.9218 13.5625 16.7155 13.5625 16.5C13.5625 16.2845 13.4766 16.0782 13.3242 15.9258C13.1867 15.7882 13.0051 15.7053 12.8125 15.6904V11.25C12.8125 11.0345 12.7266 10.8282 12.5742 10.6758C12.4218 10.5234 12.2155 10.4375 12 10.4375H11.25ZM11.8125 6.6875C11.4976 6.6875 11.1954 6.81246 10.9727 7.03516C10.75 7.25786 10.625 7.56006 10.625 7.875C10.625 8.10987 10.6947 8.33987 10.8252 8.53516C10.9557 8.73029 11.1415 8.88185 11.3584 8.97168C11.5753 9.06148 11.8137 9.08577 12.0439 9.04004C12.2743 8.99422 12.4863 8.88092 12.6523 8.71484C12.8184 8.54877 12.9317 8.3368 12.9775 8.10645C13.0233 7.87623 12.999 7.63776 12.9092 7.4209C12.8193 7.20403 12.6678 7.01817 12.4727 6.8877C12.2774 6.75721 12.0474 6.6875 11.8125 6.6875Z"
                fill="#F5F5F5"
                stroke="#F5F5F5"
                strokeWidth="0.125"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Pickem-specific Google Ad. Carries its own spacing on both sides, since row 1 above
          it drops its margin on an upcoming contest and row 2 below it may not render. */}
      {shouldShowAd && adPlacement && (
        <div className={`mt-3 ${isLiveOrFinal ? 'mb-4' : ''}`}>
          <AdSlot
            divId={`pickem-contest-card-${id}`}
            adPlacement={adPlacement}
            placement="pickem-contest-card"
            className="rounded-lg overflow-hidden"
          />
        </div>
      )}

      {/*
        Row 2 — live/final only.
        The upcoming-contest version of this row (Entries / Entry / Prize Pool) was dropped:
        entry count and a "FREE" cost are not what a player is deciding on, and the prize now
        reads on the title block above. Live and final keep their row because rank and points
        are the player's own result and have nowhere else to go.
      */}
      {isLiveOrFinal && (
        <div className="grid grid-cols-3 gap-4">
          {/* Column 1: Entries (left aligned) */}
          <div className="flex flex-col gap-1 text-left border-r border-[#6F6F6F] pr-4">
            <span className="text-[10px] text-text-muted font-gilroy">Rank</span>
            <span className="text-sm font-gilroy font-bold text-text-primary">
              {competitionData?.finalScore?.rank ?? '-'} / {totalEntries}
            </span>
          </div>

          {/* Column 2: Entry (center aligned) */}
          <div className="flex flex-col gap-1 text-center border-r border-[#6F6F6F] pr-4">
            <span className="text-[10px] text-text-muted font-gilroy">Points</span>
            <span
              data-test-id={`entry-cost-${id}`}
              className="text-sm font-gilroy font-bold text-text-primary"
            >
              {competitionData?.finalScore?.points ?? '-'}
            </span>
          </div>

          {/* Column 3: Prize Pool (right aligned) */}
          <div className="flex flex-col gap-1 text-right">
            <span className="text-[10px] text-text-muted font-gilroy">Current Prize</span>
            <span
              data-test-id={`current-prize-${id}`}
              className="text-sm font-gilroy font-bold text-pickem-primary"
            >
              {competitionData?.prize ?? '-'}
            </span>
          </div>
        </div>
      )}

      {isLiveOrFinal && showResults && (
        <div className="mt-4 flex gap-2 justify-center">
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onViewLeaderboard?.();
            }}
            className="rounded-full border border-border bg-app-background text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
            data-test-id={`view-leaderboard-${id}`}
          >
            {t('game.actions.viewLeaderboard')}
            <LeaderBoardIcon />
          </Button>
        </div>
      )}

      {isLiveOrFinal && showLeaderBoard && (
        <div className="mt-4 flex gap-2 justify-center">
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onViewResults?.();
            }}
            className="rounded-full border border-border bg-app-background text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
            data-test-id={`view-leaderboard-${id}`}
          >
            {t('game.actions.viewResults')}
            <LeaderBoardIcon />
          </Button>
        </div>
      )}

      {/* Reserved-but-not-yet-open (DKPLAT-528): mirrors GameCard.tsx's reserved row */}
      {showReservedRow && !hasQuestions && questionsPostedLabel && (
        <p
          data-test-id={`reserved-questions-posted-${id}`}
          className="mt-3 text-xs font-gilroy font-semibold text-yellow-500 text-center"
        >
          Questions will be posted {questionsPostedLabel}
        </p>
      )}

      {/* DKPLAT-533 states 1 & 2 — mirrors GameCard.tsx and apps/squares ContestCard */}
      {showReservedRow && (
        <p
          data-test-id={
            hasQuestions ? `reserved-make-selections-${id}` : `reserved-entry-reserved-${id}`
          }
          className={
            hasQuestions
              ? 'mt-3 text-sm font-gilroy font-bold text-[#53d337] text-center'
              : 'mt-3 text-sm font-gilroy font-semibold text-yellow-500 text-center'
          }
        >
          {hasQuestions ? 'No Picks Submitted - Make Selections Now!' : 'Entry Reserved'}
        </p>
      )}

      {/* DKPLAT-533 state 3 — persists through live → Past Results after grading */}
      {showNoPicksLabel && (
        <p
          data-test-id={`reserved-no-picks-submitted-${id}`}
          className="mt-3 text-xs font-gilroy font-semibold text-yellow-500 text-center"
        >
          No Picks Submitted
        </p>
      )}

      {showReservedRow && (
        <div className="mt-4 flex gap-2 justify-between w-full">
          <Button
            variant="outline"
            disabled={!hasQuestions || !onEdit}
            onClick={e => {
              e.stopPropagation();
              onEdit?.();
            }}
            className={`rounded-full border border-border bg-app-background whitespace-nowrap px-3 flex-1 ${
              hasQuestions && onEdit
                ? 'text-foreground hover:bg-app-surface-hover hover:text-foreground'
                : 'text-foreground/50 cursor-not-allowed'
            }`}
            data-test-id={
              hasQuestions && onEdit
                ? `reserved-edit-picks-${id}`
                : `reserved-edit-picks-disabled-${id}`
            }
          >
            {t('game.actions.editPicks')} (1)
          </Button>
          <Button
            variant="pickem"
            disabled
            className="whitespace-nowrap px-3 flex-1 cursor-not-allowed opacity-90"
            data-test-id={`reserved-max-entered-${id}`}
          >
            Max Entered
          </Button>
        </div>
      )}
    </div>
  );
};

export default GameCardFullWidth;
